import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Type,
  FileText,
  Image,
  Search,
  ArrowRight,
  CheckCircle2,
  BarChart,
} from "lucide-react";
import { Progress } from "../components/ui/progress";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import { useJobProgress } from "../hooks/useJobProgress";
import { ImportJobStatusResponse } from "../admin/dashboards/types";
import { updateCurrentUser, updateMiqCollection } from "wasp/client/operations";
import { analytics } from "../analytics";
import { useTranslation } from "../i18n/hooks";

const CircularLoader = () => {
  return (
    <div className="w-5 h-5 relative">
      <div className="w-full h-full rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin" />
    </div>
  );
};

interface QualityLabel {
  text: string;
  icon: React.FC<any>;
}

interface JobStep {
  id: number;
  label: string;
  description: string;
  threshold: number;
  icon: React.FC<any>;
}

interface ContentImpactStaticProps {
  dashboardUserState: "importInProgress" | string;
  handleStartAnalysis: () => void;
  jobId: string | null | undefined;
  activeCollectionId: string | undefined;
}

const RenderJobStatus = ({
  jobId,
  activeCollectionId,
  isImportInProgress,
  setQuality
}:{
  jobId: string;
  activeCollectionId: string;
  isImportInProgress: boolean;
  setQuality: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const [sortedSteps, setSortedSteps] = useState<JobStep[]>([]);
  const {currentUser} = useSelector((state: RootState) => state.user);
  const { t } = useTranslation();
  
  const jobSteps: JobStep[] = [
    {
      id: 1,
      label: t('jobSteps.importing_catalog'),
      description: t('jobSteps.importing_catalog_desc'),
      threshold: 20,
      icon: FileText,
    },
    {
      id: 2,
      label: t('jobSteps.exploring_category'),
      description: t('jobSteps.exploring_category_desc'),
      threshold: 40,
      icon: Type,
    },
    {
      id: 3,
      label: t('jobSteps.evaluating_titles'),
      description: t('jobSteps.evaluating_titles_desc'),
      threshold: 60,
      icon: Type,
    },
    {
      id: 4,
      label: t('jobSteps.analyzing_descriptions'),
      description: t('jobSteps.analyzing_descriptions_desc'),
      threshold: 75,
      icon: FileText,
    },
    {
      id: 5,
      label: t('jobSteps.checking_visual'),
      description: t('jobSteps.checking_visual_desc'),
      threshold: 90,
      icon: Image,
    },
    {
      id: 6,
      label: t('jobSteps.analyzing_seo'),
      description: t('jobSteps.analyzing_seo_desc'),
      threshold: 100,
      icon: Search,
    },
  ];
  
  const {
    progress: importProgress,
    error: importError,
    completed,
  } = useJobProgress({
    jobId: jobId,
    initialProgress: 0,
    onCompleted: async (shopifyResponse: ImportJobStatusResponse) => {  
      analytics.sendAnalysisCompletedEvent({
        email: currentUser?.email || "unknown",
      })

      await new Promise((resolve) => setTimeout(resolve, 2000));
      
      await updateMiqCollection({
        id: activeCollectionId,
        tableId: shopifyResponse.table_id,
        miqHasCompletedShopifyImport: true,
        miqHasCompletedExcelImport: true,
      });
    },
    onError: async (message) => {
       await updateMiqCollection({
          id: activeCollectionId,
          miqHasStartedExcelImport: false,
          miqHasStartedShopifyImport: false,
          miqShopifyJobId: null,
          miqImportMethod: null,
       })
       window.alert(message);
    }
  });

  useEffect(() => {
    if(importProgress > 0 && currentUser?.email){
      analytics.sendAnalysisInProgressEvent({
        progress: importProgress,
        email: currentUser?.email
      })
    }
  }, [importProgress, currentUser?.email])

  useEffect(() => {
    if (isImportInProgress) {
      setSortedSteps(jobSteps);
    } else {
      setSortedSteps(jobSteps);
    }
  }, [importProgress, isImportInProgress]);

  // Handle quality progress animation
  useEffect(() => {
    if (!isImportInProgress) {
      const fillInterval = window.setInterval(() => {
        setQuality((prev) => {
          if (prev >= 100) {
            window.clearInterval(fillInterval);
            return 100;
          }
          return prev + 1;
        });
      }, 25); // Increased animation speed (2x faster)
      return () => window.clearInterval(fillInterval);
    } else {
      setQuality(importProgress);
    }
  }, [isImportInProgress, importProgress]);


  return  <div className="space-y-6">
  <div className="w-full max-w-2xl mx-auto">
    <Progress
      value={importProgress}
      className="h-3"
    />
    <p className="text-center mt-2 text-slate-600">
      {t('contentImpact.overall_progress')}: {Math.round(importProgress)}%
    </p>
  </div>

  <div className="relative">
    {sortedSteps.map((step) => {
      const Icon = step.icon;
      const isActive =
        importProgress >= step.threshold - 20 &&
        importProgress < step.threshold;
      const isComplete = importProgress >= step.threshold;

      return (
        <div
          key={step.id}
          className={`p-3 rounded-lg border mb-3 transform transition-all duration-500 ease-in-out ${
            isActive
              ? "bg-blue-50 border-blue-200 scale-[1.01] shadow-sm z-10"
              : isComplete
                ? "bg-green-50 border-green-200"
                : "bg-slate-50 border-slate-200"
          }`}
          style={{
            position: "relative",
            transform: `${isActive ? "scale(1.02)" : "scale(1)"}`,
            zIndex: isActive ? 2 : 1,
          }}
        >
          <div className="flex items-center space-x-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                isComplete
                  ? "bg-green-100"
                  : isActive
                    ? "bg-blue-100"
                    : "bg-slate-100"
              }`}
            >
              {isComplete ? (
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              ) : isActive ? (
                <CircularLoader />
              ) : (
                <Icon className={`w-6 h-6 text-slate-400`} />
              )}
            </div>
            <div className="flex-1">
              <h3
                className={`font-semibold transition-colors duration-300 ${
                  isComplete
                    ? "text-green-700"
                    : isActive
                      ? "text-blue-700"
                      : "text-slate-700"
                }`}
              >
                {step.label}
              </h3>
              <p
                className={`text-sm transition-colors duration-300 ${
                  isComplete
                    ? "text-green-600"
                    : isActive
                      ? "text-blue-600"
                      : "text-slate-500"
                }`}
              >
                {step.description}
              </p>
            </div>
          </div>
        </div>
      );
    })}
  </div>
</div>
}

const ContentImpactStatic: React.FC<ContentImpactStaticProps> = ({
  dashboardUserState,
  handleStartAnalysis,
  jobId,
  activeCollectionId
}) => {
  const [quality, setQuality] = useState(0);
  const { t } = useTranslation();

  const qualityLabels: QualityLabel[] = [
    { text: t('contentImpact.engaging_titles'), icon: Type },
    { text: t('contentImpact.targeted_descriptions'), icon: FileText },
    { text: t('contentImpact.visual_appeal'), icon: Image },
    { text: t('contentImpact.optimized_seo'), icon: Search },
  ];

  const isImportInProgress = dashboardUserState === "importInProgress";
  const {importShopifyInitialLoading} = useSelector((state: RootState) => state.flow);
  // Sort and animate steps based on their status

  useEffect(() => {
    if (!isImportInProgress) {
      const fillInterval = window.setInterval(() => {
        setQuality((prev) => {
          if (prev >= 100) {
            window.clearInterval(fillInterval);
            return 100;
          }
          return prev + 1;
        });
      }, 25); // Increased animation speed (2x faster)
      return () => window.clearInterval(fillInterval);
    } 
  }, [isImportInProgress]);

  const renderRegularContent = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
      {/* Content Quality Card */}
      <Card className="shadow-md hover:shadow-lg transition-all duration-300 border-t-4 border-t-orange-500">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span className="bg-orange-100 p-2 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-orange-500" />
            </span>
            <span>{t('contentImpact.content_quality')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative h-12 border-2 border-slate-200 rounded-full p-1 mb-4 bg-slate-50 shadow-inner">
            <div
              className="h-full rounded-full bg-gradient-to-r from-orange-500 via-yellow-500 to-green-500 transition-all duration-300 shadow-sm"
              style={{ width: `${quality}%` }}
            >
              <div className="absolute right-0 top-0 h-full w-4 bg-white opacity-30 animate-pulse" />
            </div>
          </div>

          <div className="space-y-3 mt-6">
            {qualityLabels.map((label, index) => {
              const Icon = label.icon;
              return (
                <div
                  key={label.text}
                  className={`flex items-center space-x-3 p-2 rounded-lg transition-all duration-300 hover:bg-slate-50 ${
                    quality >= (index + 1) * 25
                      ? "text-slate-800"
                      : "text-slate-300"
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      quality >= (index + 1) * 25
                        ? "bg-green-100"
                        : "bg-slate-100"
                    }`}
                  >
                    <Icon
                      className={`w-4 h-4 ${
                        quality >= (index + 1) * 25
                          ? "text-green-600"
                          : "text-slate-300"
                      }`}
                    />
                  </div>
                  <span className="flex-1">{label.text}</span>
                  <div
                    className={`w-2 h-2 rounded-full ${
                      quality >= (index + 1) * 25
                        ? "bg-green-500"
                        : "bg-slate-200"
                    }`}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Sales Performance Card */}
      <Card className="shadow-md hover:shadow-lg transition-all duration-300 border-t-4 border-t-green-500">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span className="bg-green-100 p-2 rounded-lg">
              <BarChart className="w-5 h-5 text-green-500" />
            </span>
            <span>{t('contentImpact.sales_performance')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative h-12 bg-slate-100 rounded-lg overflow-hidden shadow-inner">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 to-green-600 transition-all duration-300"
              style={{ width: `${(quality / 100) * 90}%` }}
            >
              <div className="absolute right-0 top-0 h-full w-4 bg-white opacity-30 animate-pulse" />
            </div>
          </div>

          <div className="mt-6">
            <div className="flex justify-between items-center">
              <span className="text-slate-500">{t('contentImpact.average')}</span>
              <span className="text-slate-500">{t('contentImpact.optimized')}</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-lg font-semibold text-red-500">{t('contentImpact.average_conversion')}</span>
              <span className="text-lg font-semibold text-green-500">
                {t('contentImpact.optimized_conversion')}
              </span>
            </div>
          </div>

          <Card className="mt-6 bg-green-50 border-green-100 shadow-sm hover:shadow-md transition-all duration-300">
            <CardContent className="pt-6">
              <div className="text-center">
                <span className="text-sm text-green-600">
                  {t('contentImpact.typical_sales_improvement')}
                </span>
                <div className="text-2xl font-bold text-green-700">{t('contentImpact.improvement_percentage')}</div>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <Card className="bg-slate-50/80 backdrop-blur-sm shadow-lg transition-all duration-300 hover:shadow-xl">
      <CardHeader className="text-center">
        <CardTitle className="text-xl md:text-3xl font-bold mb-1 md:mb-3 text-slate-900">
          {isImportInProgress
            ? t('contentImpact.analyzing_content')
            : t('contentImpact.quality_content_drives_sales')}
        </CardTitle>
        <p className="text-slate-600 text-md md:text-lg font-medium">
          {isImportInProgress
            ? t('contentImpact.analyzing_content_performance')
            : t('contentImpact.connect_data_see_results')}
        </p>
      </CardHeader>

      <CardContent className="space-y-8">
        {(isImportInProgress && jobId && activeCollectionId) ? <RenderJobStatus
        jobId={jobId}
        activeCollectionId={activeCollectionId}
        isImportInProgress={isImportInProgress}
        setQuality={setQuality}
        
        /> : renderRegularContent()}

        {/* CTA Section */}
        <div className="text-center space-y-4">
          <div className="relative w-full max-w-lg mx-auto">
            {!isImportInProgress ? (
              <>
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-blue-400 rounded-xl blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                <Button
                  disabled={importShopifyInitialLoading}
                  size="lg"
                  data-testid="analyze-content-button"
                  className="relative w-full h-14 text-xl font-semibold transition-all duration-300 overflow-hidden rounded-xl 
            bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 hover:shadow-[0_0_20px_rgba(59,130,246,0.5)] hover:-translate-y-0.5
            focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-slate-50"
                  onClick={handleStartAnalysis}
                >
                  <div className="relative z-10 flex items-center justify-center group">
                    <span className="text-white group-hover:scale-105 transition-transform duration-200">
                     {importShopifyInitialLoading ? t('contentImpact.connecting_to_shopify') : t('contentImpact.analyze_content')} 
                    </span>
                    {!importShopifyInitialLoading && <ArrowRight className="w-6 h-6 ml-2 text-white group-hover:translate-x-1 transition-transform duration-200" />} 
                  </div>
                </Button>
              </>
            ) : (
              <div className="w-full"></div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContentImpactStatic;
